import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import styled from 'styled-components'

import { allPropertiesFalse } from 'utils/helpers'
import { Q4_2025_PROMO_TYPE, useHubSpotPromo } from 'utils/hooks/useHubSpotPromo'
import { ALL_COMPANIES_OPTION } from 'utils/partnerUtils'
import { URL_CONST } from 'utils/urlConsts'
import { matchURL } from 'utils/urls'

import {
  DISABLE_PHISHING_SOC_TRIAL_ASYNC,
  REQUEST_THEMIS_SUMMARY_ASYNC,
} from 'containers/Agents/constants'
import { selectPhishingSocTrialLoading } from 'containers/Agents/selectors'
import { selectActualFeatureFlags, selectIsStaticSalesDemo } from 'containers/App/selectors'
import { selectCompanyFilter } from 'containers/PartnerOptions/selectors'
import { NAME_FOR_HISTORY_STORE } from 'containers/ResolutionCenter/constants'
import { ALL_TIMES } from 'containers/ResolutionCenter/IncidentFilters/constants'
import { QUICK_FILTER } from 'containers/ResolutionCenter/QuickFilters/constants'

import { Close, Intent } from 'components/lib/DesignSystem/Icons'
import Tag, { TAGS_TYPES } from 'components/lib/DesignSystem/Tag'
import Button, { SIZES, VARIANTS } from 'components/lib/DesignSystem/V3/Button'

// Dec 31, 2025 23:59:00 local time (month is 0-indexed, so 11 = December)
export const PHISHING_SOC_END_DATE = new Date(2025, 11, 31, 23, 59, 0)

/**
 * Banner styled components - heights determined by content + padding
 * No more position: fixed or hardcoded heights
 */

const BannerContainer = styled.section`
  width: 100%;
  /* LtR blue gradient so the DSv3 secondary (white) CTA contrasts — matches the
     AI Credits discovery banner. TODO: confirm exact fill against Figma. */
  background: linear-gradient(91deg, #3063fd 0%, #1948d2 100%);
  padding: 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  overflow: visible;

  @media (max-width: 1600px) {
    padding: 24px;
  }

  @media (max-width: 850px) {
    display: none;
  }
`

const TitleSection = styled.div`
  display: flex;
  align-items: flex-start;
  flex-shrink: 0;
  position: relative;
`

const Title = styled.h2`
  font-family: 'Inter', sans-serif;
  font-weight: var(--bold);
  font-size: 32px;
  line-height: 40px;
  letter-spacing: 0.3691px;
  margin: 0;
  color: var(--ds-blue-6);
  white-space: nowrap;

  @media (max-width: 1600px) {
    font-size: 28px;
    line-height: 36px;
  }

  @media (max-width: 1400px) {
    font-size: 18px;
    line-height: 24px;
  }
`

const PreviewBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 22px;
  padding: 0 8px;
  font-family: 'Poppins', sans-serif;
  font-weight: var(--bold);
  font-size: 14px;
  letter-spacing: 1px;
  color: var(--white);
  text-align: center;
  line-height: 16px;
  position: relative;
  border-radius: 16px;
  border: 1px solid rgba(159, 199, 255, 0.7);
  background: linear-gradient(133deg, #09c -9.3%, #00a3bf 93.02%);
  box-shadow: 0 0 34px 0 #c99ded;
  z-index: 10;
  position: absolute;
  bottom: 8px;
  right: -40px;
  top: -22px;

  @media (max-width: 1600px) {
    font-size: 12px;
    line-height: 16px;
  }

  @media (max-width: 1280px) {
    font-size: 10px;
    line-height: 14px;
  }
`

const MessageSection = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
`

const Message = styled.p`
  font-family: 'Inter', sans-serif;
  font-weight: var(--semibold);
  font-size: 20px;
  line-height: 28px;
  letter-spacing: -0.45px;
  color: var(--white);
  margin: 0;
  flex-shrink: 1;

  @media (max-width: 1600px) {
    font-size: 14px;
    line-height: 20px;
  }

  @media (max-width: 1080px) {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
  }
`

const TimerText = styled(Message)`
  font-family: 'Inter', sans-serif;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: visible;
  font-weight: 700;
  letter-spacing: -0.449px;
  border-radius: 12px;
  background: rgba(217, 217, 217, 0.14);
  padding: 8px;

  @media (max-width: 1280px) {
    display: none;
  }
`

const ButtonContainer = styled.div`
  display: flex;
  flex-shrink: 0;
  justify-content: flex-end;
`

const CloseButton = styled.button`
  align-self: center;
  width: 12px;
  height: 12px;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  margin-left: 16px;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }

  svg path {
    fill: var(--white);
  }

  &:hover svg path {
    fill: var(--ds-gray-5);
  }
`

const NewTag = styled(Tag)`
  box-shadow: 0 4px 34px 4px #c99ded;
  border: 1px solid #2e5bff;
  position: absolute;
  top: -14px;
  right: -10px;
`

/**
 * PhishingSOCAgentBanner - Returns only banner content, no children wrapping
 *
 * This component is designed to be used with AppLayout which handles
 * the positioning. The banner content will be placed in the header area
 * and its height is determined automatically by CSS.
 *
 * @param {'initial' | 'upgrade'} type - The type of banner to display.
 * @param {object} history - Router history object for navigation.
 * @returns {React.ReactNode|null} Banner content or null if banner should be hidden
 */
const PhishingSOCAgentBanner = ({ type, history }) => {
  const dispatch = useDispatch()
  const isLoading = useSelector(selectPhishingSocTrialLoading)
  const [isDismissed, setIsDismissed] = useState(false)
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 })
  const companyFilter = useSelector(selectCompanyFilter)

  const calculateTimeLeft = useCallback(() => {
    if (!PHISHING_SOC_END_DATE) return { days: 20, hours: 23, minutes: 59, seconds: 59 }

    const now = Date.now()
    const end = new Date(PHISHING_SOC_END_DATE).getTime()
    const difference = end - now

    if (difference <= 0) {
      return { days: 0, hours: 0, minutes: 0, seconds: 0 }
    }

    return {
      days: Math.floor(difference / (1000 * 60 * 60 * 24)),
      hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((difference % (1000 * 60)) / 1000),
    }
  }, [])

  useEffect(() => {
    setTimeLeft(calculateTimeLeft())

    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft())
    }, 1000)

    return () => clearInterval(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculateTimeLeft])

  const handleClose = () => {
    setIsDismissed(true)
  }

  const { submitPromo } = useHubSpotPromo(Q4_2025_PROMO_TYPE)

  const handleTryNow = () => {
    // Navigate to classified incidents with mail quick filter and soc=1
    const url = `${URL_CONST.incidents.classified}?soc=1`
    history.push(url, {
      [NAME_FOR_HISTORY_STORE]: {
        quickFilters: [QUICK_FILTER.MAIL],
        dateFilter: ALL_TIMES,
      },
    })
    submitPromo('show_me')
    dispatch(REQUEST_THEMIS_SUMMARY_ASYNC.initiate())
  }

  const hasTimeEnded = Date.now() > PHISHING_SOC_END_DATE.getTime()

  const ff = useSelector((state) => selectActualFeatureFlags(state, allPropertiesFalse))
  const isStaticSalesDemo = useSelector(selectIsStaticSalesDemo)
  const isPartnerUrl = matchURL(URL_CONST.partners.self)
  const isAllCompanySelected = companyFilter?.label === ALL_COMPANIES_OPTION.label
  const isCampaignActive = ff.ENABLE_THEMIS_AI_TRIAL_CAMPAIGN

  // Check if banner should be hidden
  const hideBanner =
    isDismissed ||
    hasTimeEnded ||
    isAllCompanySelected ||
    isPartnerUrl ||
    !isCampaignActive ||
    isStaticSalesDemo

  if (hideBanner) return null

  const formatTimeUnit = (unit) => String(unit).padStart(2, '0')

  return (
    <BannerContainer aria-label="Phishing SOC Agent banner">
      <TitleSection>
        <Title>Phishing SOC Agent</Title>
        {type === 'initial' ? (
          <PreviewBadge>PREVIEW</PreviewBadge>
        ) : (
          <NewTag type={TAGS_TYPES.NEW} />
        )}
      </TitleSection>

      <MessageSection>
        <Message>
          {type === 'initial'
            ? 'Get deep investigation on any email incident - limited preview available till Dec 31'
            : 'Keep the Phishing SOC Agent and get ATO + Deepfake Protection security suit through December 31st'}
        </Message>
        <TimerText aria-hidden="true">
          <Intent fill="var(--white)" width="20px" height="20px" />
          {formatTimeUnit(timeLeft.days)}d: {formatTimeUnit(timeLeft.hours)}h:{' '}
          {formatTimeUnit(timeLeft.minutes)}m
        </TimerText>
      </MessageSection>

      <ButtonContainer>
        {type === 'initial' ? (
          <Button size={SIZES.MEDIUM} variant={VARIANTS.SECONDARY} onClick={handleTryNow}>
            Show Me
          </Button>
        ) : (
          <Button
            size={SIZES.MEDIUM}
            variant={VARIANTS.SECONDARY}
            disabled={isLoading}
            onClick={() => {
              submitPromo('get_quote')
              dispatch(DISABLE_PHISHING_SOC_TRIAL_ASYNC.initiate())
            }}
          >
            Get Price
          </Button>
        )}

        <CloseButton onClick={handleClose} aria-label="Close banner">
          <Close />
        </CloseButton>
      </ButtonContainer>
    </BannerContainer>
  )
}

export default PhishingSOCAgentBanner
